import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Home } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

export default function NotFoundPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] transition-colors duration-300 flex flex-col">
      <Helmet>
        <title>Page Not Found | UI DESIGNS</title>
      </Helmet>
      <Navigation />

      <main className="flex-1 flex items-center justify-center px-4 pt-20">
        <div className="max-w-2xl w-full text-center">
          {/* Illustration */}
          <div className="relative mx-auto mb-10 w-64 h-64 sm:w-80 sm:h-80">
            {/* Background glow */}
            <div className="absolute inset-0 bg-[#00E5FF]/10 rounded-full blur-[80px]" />

            {/* Grid pattern circle */}
            <div className="relative w-full h-full rounded-full border-2 border-dashed border-slate-200 dark:border-white/10 flex items-center justify-center overflow-hidden">
              <div
                className="absolute inset-0 opacity-[0.04] dark:opacity-[0.06]"
                style={{
                  backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
                  backgroundSize: '20px 20px',
                }}
              />

              {/* 404 number */}
              <div className="relative">
                <span className="font-['Outfit'] text-[120px] sm:text-[160px] font-black leading-none tracking-tighter text-slate-100 dark:text-white/[0.06] select-none">
                  404
                </span>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-['Outfit'] text-[120px] sm:text-[160px] font-black leading-none tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-[#00E5FF] to-[#00E5FF]/30 select-none">
                    404
                  </span>
                </div>
              </div>
            </div>

            {/* Floating elements */}
            <div className="absolute top-4 right-4 w-3 h-3 rounded-full bg-[#00E5FF] animate-pulse" />
            <div className="absolute bottom-8 left-2 w-2 h-2 rounded-full bg-[#00E5FF]/50 animate-pulse" style={{ animationDelay: '0.5s' }} />
            <div className="absolute top-1/3 left-0 w-1.5 h-1.5 rounded-full bg-[#00E5FF]/30 animate-pulse" style={{ animationDelay: '1s' }} />
          </div>

          {/* Text */}
          <h1
            data-testid="404-title"
            className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-[#0A0A0A] dark:text-white tracking-tight mb-4"
          >
            Page not found
          </h1>
          <p className="text-base sm:text-lg text-slate-500 dark:text-slate-400 max-w-md mx-auto mb-10 leading-relaxed">
            The page you're looking for doesn't exist or has been moved. Let's get you back on track.
          </p>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-32">
            <Link
              to="/"
              data-testid="404-home-btn"
              className="inline-flex items-center gap-2 bg-[#0A0A0A] dark:bg-[#8B5CF6] text-white px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#00E5FF] hover:text-[#0A0A0A] dark:hover:bg-violet-400 transition-all"
            >
              <Home className="w-4 h-4" />
              Back to Home
            </Link>
            <Link
              to="/contact"
              data-testid="404-contact-btn"
              className="inline-flex items-center gap-2 border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-400 px-7 py-3.5 rounded-full text-sm font-medium hover:border-[#0A0A0A] hover:text-[#0A0A0A] dark:hover:border-white dark:hover:text-white transition-all"
            >
              <ArrowLeft className="w-4 h-4" />
              Get in Touch
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
